import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { CertificatePrintPreview } from "./CertificatePrintPreview";
import { HologramSelect } from "./HologramSelect";

interface CertificateFormProps {
  print: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CertificateForm({ print, open, onOpenChange }: CertificateFormProps) {
  const [hologramNumber, setHologramNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [certificate, setCertificate] = useState<any>(null);
  const [printData, setPrintData] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch available hologram codes
  const { data: availableHolograms, isLoading: isLoadingHolograms } = useQuery({
    queryKey: ["available-holograms"],
    queryFn: async () => {
      console.log('Fetching available hologram codes');
      const { data, error } = await supabase
        .from('holograms')
        .select('hologram_id, hologram_code, provider_id')
        .eq('status', 'available')
        .order('hologram_code');

      if (error) {
        console.error('Error fetching hologram codes:', error);
        throw error;
      }

      console.log('Available hologram codes:', data);
      return data;
    },
  });

  // Set initial hologram number when data is loaded
  useEffect(() => {
    if (availableHolograms && availableHolograms.length > 0 && !hologramNumber) {
      console.log('Setting initial hologram number:', availableHolograms[0].hologram_code);
      setHologramNumber(availableHolograms[0].hologram_code);
    }
  }, [availableHolograms, hologramNumber]);

  const handleIssueCertificate = async () => {
    if (!hologramNumber.trim()) {
      toast({
        title: "Error",
        description: "Please select a hologram number",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get the hologram_id for the selected hologram_code
      const selectedHologram = availableHolograms?.find(
        (h) => h.hologram_code === hologramNumber
      );

      if (!selectedHologram) {
        throw new Error("Selected hologram not found");
      }

      console.log('Creating new certificate with hologram:', selectedHologram);
      
      // Create certificate with hologram_id
      const { data: certificateData, error: certificateError } = await supabase
        .from('certificates')
        .insert([
          {
            hologram_number: hologramNumber,
            hologram_id: selectedHologram.hologram_id,
            issue_date: new Date().toISOString().split('T')[0],
            issued_by: 'System',
          }
        ])
        .select()
        .single();

      if (certificateError) {
        console.error('Error creating certificate:', certificateError);
        throw certificateError;
      }

      console.log('Certificate created:', certificateData);

      // Mark hologram as used
      const { error: hologramError } = await supabase
        .from('holograms')
        .update({ 
          status: 'used',
          used_at: new Date().toISOString(),
        })
        .eq('hologram_id', selectedHologram.hologram_id);

      if (hologramError) {
        console.error('Error updating hologram status:', hologramError);
        throw hologramError;
      }

      // Update print with certificate_id and fetch the updated print data
      const { data: updatedPrint, error: printError } = await supabase
        .from('prints')
        .update({ 
          certificate_id: certificateData.certificate_id,
          status: 'printed'
        })
        .eq('print_id', print.print_id)
        .select(`
          *,
          certificates (*),
          media (*),
          print_service_providers (*),
          editions (
            *,
            artworks (
              *,
              creators (*)
            )
          )
        `)
        .single();

      if (printError) {
        console.error('Error updating print:', printError);
        throw printError;
      }

      console.log('Print updated with certificate:', updatedPrint);
      
      // Store the current print data and certificate
      setPrintData(updatedPrint);
      setCertificate(certificateData);
      
      // Close both dialogs
      onOpenChange(false);
      
      // Show the print preview after a short delay
      setTimeout(() => {
        setShowPrintPreview(true);
        console.log('Setting showPrintPreview to true');
      }, 100);

      // Invalidate queries to refresh the data
      await queryClient.invalidateQueries({ queryKey: ["prints-list"] });
      await queryClient.invalidateQueries({ queryKey: ["available-holograms"] });

      toast({
        title: "Success",
        description: "Certificate issued successfully",
      });

    } catch (error) {
      console.error('Error issuing certificate:', error);
      toast({
        title: "Error",
        description: "Failed to issue certificate. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Certificate</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <HologramSelect
              value={hologramNumber}
              onValueChange={setHologramNumber}
              availableHolograms={availableHolograms}
              isLoading={isLoadingHolograms}
              disabled={isSubmitting}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleIssueCertificate} disabled={isSubmitting}>
              {isSubmitting ? "Issuing..." : "Issue Certificate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showPrintPreview && certificate && printData && (
        <CertificatePrintPreview
          print={printData}
          certificate={certificate}
          open={showPrintPreview}
          onOpenChange={setShowPrintPreview}
        />
      )}
    </>
  );
}
